import { PLACEHOLDER_RICH_TEXT_DIV } from "canvas/Constants";
import { PlaceholderTextFormat } from "canvas/widgets/interfaces/model/enumerations/PlaceholderTextFormat";
import * as _ from "lodash";
import {
	getPageAttributePlaceholderObjects,
	getPageFromImagePlaceholderObjects,
	getPageImagePlaceholderObjects,
	getPagePlaceholderObjects,
	getPlaceholderTextFormat,
} from "../store/PageHelper";
import { ICanvasEditorState } from "../widgets/interfaces/model/CanvasEditorState";
import {
	CanvasMode,
	GeometryTypes,
	PathOperationTypes,
	ShapeType,
} from "../widgets/interfaces/model/enumerations/index";
import { IPageObjectTypes } from "../widgets/interfaces/model/enumerations/PageObjectTypes";
import { IPresetGeometry } from "../widgets/interfaces/model/geometry/PresetGeometry";
import { IPage } from "../widgets/interfaces/model/Page";
import { IConnectionShape, IGroupShape, IShape } from "../widgets/interfaces/model/PageObjects/index";
import { IPictureFillProperties } from "../widgets/interfaces/model/properties";
import { IConnectionShapeProperties } from "../widgets/interfaces/model/properties/ConnectionShapeProperties";
import { IShapeProperties } from "../widgets/interfaces/model/properties/ShapeProperties";
import { PageObjectType } from "../widgets/interfaces/model/Types";
import { getSelectedPage } from "../widgets/selectors/page";
import {
	getAllSelectedObjects,
	getSelectedBaseObjects,
	getSelectedPageObjects,
} from "../widgets/selectors/selection";
import { removeZeroSpaceWidth } from "../widgets/util";
import { deepFindContainingGroup, firstCharacterStyles, generateUUID, getKeysFromGroup } from "./reducers/util";
import { getStore } from "./ReduxRegistry";
import SelectionHelper from "./SelectionHelper";

/**
 * Return all child objects from their page objects
 */
const _getPageObjects = (pageObjects: IGroupShape[] | IShape[], childObjects: PageObjectType[], ignoreImages?: boolean, placeholderTextFormat?: string) => {
	pageObjects.forEach((pageObject) => {
		if (pageObject.childObjects) {
			return _getPageObjects(pageObject.childObjects, childObjects, ignoreImages, placeholderTextFormat);
		}

		const isRichTextValue = placeholderTextFormat === PlaceholderTextFormat.VALUE && pageObject.txBody?.content.includes(PLACEHOLDER_RICH_TEXT_DIV);

		if (!ignoreImages || (!pageObject.imagePlaceholder && !pageObject.isPicture) && !isRichTextValue) {
			childObjects.push(pageObject);
		}
	});
	return childObjects;
};

/**
 * Get common text-style value for the specified key.
 * In case there are multiple values found for the given key, undefined is returned.
 * @param {PageObjectTypes[]} pageObjects Array of page objects for which common value needs to found.
 * @param {string} keyPath path for the key example: spPr.fill.color for Fill color.
 * @param {boolean} ignoreImages flag to ignore images
 * @return {string | string[] | undefined} string or string array of common values, or undefined
 */
const getCommonTextValue = (pageObjects: IGroupShape[] | IShape[], keyPath: string, ignoreImages?: boolean): any => {
	let placeholderTextFormat = getPlaceholderTextFormat();
	const childObjects = _getPageObjects(pageObjects, [], ignoreImages, placeholderTextFormat);
	const values = childObjects.map((pageObject: IShape) => {
		placeholderTextFormat = pageObject.attributeKey ? placeholderTextFormat : "";
		return _.get(firstCharacterStyles(
			pageObject.txBody && pageObject.txBody.content,
			placeholderTextFormat,
		), keyPath);
	});
	const uniqValues = _.uniqWith(values, _.isEqual);
	if (uniqValues.length === 1) {
		return uniqValues[0];
	}
	return undefined;
};

/**
 * Get common value for the specified key.
 * In case there are multiple values found for the given key, undefined is returned.
 * @param pageObjects {PageObjectTypes[]} Array of page objects for which common value needs to found.
 * @param keyPath {string} path for the key example: spPr.fill.color for Fill color.
 * @param {boolean} ignoreImages flag to ignore images
 * @return {string | string[] | undefined} string or string array of common values, or undefined
 */
const getCommonValue = (pageObjects: IGroupShape[] | IShape[], keyPath: string, ignoreImages?: boolean): any => {
	const placeholderTextFormat = getPlaceholderTextFormat();
	const childObjects = _getPageObjects(pageObjects, [], ignoreImages, placeholderTextFormat);
	const values = childObjects.map((pageObject: IShape) => {
		const value = _.get(pageObject, keyPath);
		return value ? value : "none";
	});
	const uniqValues = _.uniqWith(values, _.isEqual);
	if (uniqValues.length === 1) {
		return uniqValues[0];
	}
	return undefined;
};

/**
 * Check if PageObject supports arrows.
 * Group shapes are flatened out, returns true only if all child object support it.
 * @param pageObjects {PageObjectType[]}
 */
export const supportsArrow = (pageObjects: PageObjectType[]): boolean => {
	return _.every(pageObjects, (po) => {
		if (po.type === IPageObjectTypes.GROUP_SHAPE && !po.spPr.locked) {
			return supportsArrow(po.childObjects);
		} else if (po.type === IPageObjectTypes.SHAPE || po.type === IPageObjectTypes.CONNECTION_SHAPE) {
			return po.allowsArrows || (po.spPr.locked);
		}
	});
};

/**
 * Check if PageObject supports fill.
 * Group shapes are flatened out, returns true only if all child object support it.
 * @param pageObjects {PageObjectType[]}
 */
export const supportsFill = (pageObjects: PageObjectType[]): boolean => {
	return _.every(pageObjects, (po) => {
		if (po.type === IPageObjectTypes.GROUP_SHAPE && !po.spPr.locked) {
			return supportsFill(po.childObjects);
		} else if (po.type === IPageObjectTypes.SHAPE) {
			return po.allowsFill || isClosedShape(po) || (po.spPr.locked);
		}
	});
};

/**
 * Check if PageObject is closed shape or not.
 * @param pageObjects {IShape | IConnectionShape}
 */
export const isClosedShape = (pageObjects: IShape | IConnectionShape): boolean => {
	let isPresetShape = false;
	if (pageObjects.spPr && pageObjects.spPr.geometry) {
		isPresetShape = pageObjects.spPr.geometry.type === GeometryTypes.PRESET;
	} else {
		return false;
	}
	let isCloseCurve = false;
	if (pageObjects.spPr.geometry.type === GeometryTypes.CUSTOM) {
		const operations = pageObjects.spPr.geometry.pathLst[0].operations;
		const lastOperation = operations[operations.length - 1];
		if (!lastOperation) {
			return false;
		}
		if (lastOperation.type === PathOperationTypes.CLOSE) {
			isCloseCurve = true;
		}
	}
	return isPresetShape || isCloseCurve;
};

export default {

	/**
     * Return true all selected shapes are made from image placeholders
     * @return {boolean}
     */
	allSelectedObjectsAreFromImagePlaceholders: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);
		let allSelectedObjects: any[] = [];

		if (selectedObjects) {
			selectedObjects.forEach((selectedObject: object) => {
				allSelectedObjects = allSelectedObjects.concat(getAllSelectedObjects(selectedObject));
			});

			if (allSelectedObjects.length) {
				return _.every(allSelectedObjects, (selectedPageObject: any) => {
					return selectedPageObject.imagePlaceholder || selectedPageObject.isPictureFromPlaceholder;
				});
			}
		}
		return false;
	},

	/**
     * Return true if the selected shape is an image placeholder
     * @return {boolean}
     */
	allSelectedObjectsAreImagePlaceholders: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);
		let allSelectedObjects: any[] = [];

		if (selectedObjects) {
			selectedObjects.forEach((selectedObject: object) => {
				allSelectedObjects = allSelectedObjects.concat(getAllSelectedObjects(selectedObject));
			});

			if (allSelectedObjects.length) {
				return _.every(allSelectedObjects, (selectedPageObject: any) => {
					return selectedPageObject.imagePlaceholder;
				});
			}
		}
		return false;
	},

	/**
     * Returns whether at least one custom point is selected
     * @return {boolean}
     */
	arePointsSelected: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;

		const selectedCurvePoint = state.canvasEditor.selectedCurvePoint;

		return selectedCurvePoint !== undefined;
	},

	/**
     * Returns whether redo operation is possible or not
     * @return {boolean}
     */
	canRedo: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		return state.pages.future.length;
	},

	/**
     * Returns whether undo operation is possible or not
     * Initial past state contains first entry as empty, that's why checking for greater than 1 condition.
     * @return {boolean}
     */
	canUndo: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		return state.pages.past.length;
	},

	/**
     * Return true if selection can have ungroup applied.
     * @returns {boolean} indicating if can upgroup
     */
	canUngroupSelection: (): boolean => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);
		let canUngroup: boolean = false;

		if (selectedObjects && selectedObjects.length) {
			canUngroup = _.some(selectedObjects, (selectedObject) => {
				// allow upgroup if at least one selected shape is not an attribute
				// placeholder and the shape is a group and it is not locked
				return (
					!selectedObject.isAttributePlaceholder &&
                    selectedObject.type === "GROUP_SHAPE" &&
                    !selectedObject.spPr.locked
				);
			});
		}

		return canUngroup;
	},

	/**
     * Return the window's placeholder target if it takes an attribute
     * @return {Object}
     */
	getAttributePlaceholderTarget: () => {
		const anywindow = window as any;
		const state = getStore()
			.getState() as ICanvasEditorState;
		const page = state.pages.present;

		if (anywindow.placeholderTarget &&
            (anywindow.placeholderTarget.attributeKey) ) {
			const resultGroup = deepFindContainingGroup(page[0].pageObjects, anywindow.placeholderTarget.shapeId, null);
			const keys = getKeysFromGroup(resultGroup);
			return { keys, group: resultGroup };
		} else {
			return null;
		}
	},

	/**
     * Return canvas mode options
     * @returns {CanvasMode}
     */
	getCanvasModeOptions: () => {
		return CanvasMode;
	},

	/**
     * Return common value for the specified key for the array of objects.
     * @returns {any | undefined} Undefined is returned if objects don't have similar value.
     */
	getCommonTextValue,

	/**
     * Return common value for the specified key for the array of objects.
     * @returns {any | undefined} Undefined is returned if objects don't have similar value.
     */
	getCommonValue,

	/**
     * Return user default text styles
     * @return {ITextStyles | undefined}
     */
	getDefaultTextStyles: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		return state.selection.present.defaultTextStyles;
	},

	/**
     * Return froala editor instance
     * @return {any}
     */
	getFroalaInstance: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		return state.selection.present.textEditorInstance;
	},

	/**
     * Focus froala instance if the instance exists.
     */
	focusFroalaInstanceIfExists: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const { textEditorInstance } = state.selection.present;
		if (textEditorInstance?.el) {
			textEditorInstance.el.focus();
		}
	},

	/**
     * Return the window's placeholder target if it takes an image
     * @return {PageObject}
     */
	getImagePlaceholderTarget: () => {
		const anywindow = window as any;

		if (anywindow.placeholderTarget && !anywindow.placeholderTarget.attributeKey &&
            (anywindow.placeholderTarget.imagePlaceholder || anywindow.placeholderTarget.isPictureFromPlaceholder) ) {
			return anywindow.placeholderTarget;
		} else {
			return null;
		}
	},

	/**
     * Return placeholder keys on page
     * @return {string[]}
     */
	getPagePlaceholderKeys: (): string[] => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const page = state.pages.present;
		let allPageObjects: any[] = [];
		const keys: string[] = [];

		if (page && page.length && page[0].pageObjects) {
			page[0].pageObjects.forEach((po) => {
				allPageObjects = allPageObjects.concat(getAllSelectedObjects(po));
			});

			if (allPageObjects) {
				allPageObjects.forEach((pageObject: { attributeKey: string }) => {
					if (pageObject.hasOwnProperty("attributeKey") && pageObject.attributeKey) {
						keys.push(pageObject.attributeKey);
					}
				});
			}
		}
		return keys;
	},

	/**
     * Return pages present with the canvas.
     * Instead of returning full page object here we are returning index & id for all pages.
     *
     * @returns {[{index, id}]}
     */
	getPages: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		return state.pages.present;
	},

	/**
     * Return the selected placeholder target
     * @return {Object}
     */
	getSelectedAttributePlaceholder: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const page = state.pages.present;
		const selectedObjects = state.selection.present.selectedPageObjects;
		let selectedObject = null;

		if (selectedObjects.length === 1) {
			selectedObject = page[0].pageObjects[selectedObjects[0]] as IGroupShape;

			if (selectedObject && selectedObject.childObjects && !selectedObject.spPr.locked) {
				const firstChild = selectedObject.childObjects[0] as IShape;
				if (firstChild.attributeKey) {
					const keys = getKeysFromGroup(selectedObject);
					return { keys, group: selectedObject };
				}
			}
		}

		return null;
	},

	/**
     * Get the image link associated with selected shape
     * @return {string | undefined}
     */
	getSelectedImageLinks: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);

		const imageLinks = [] as string[];

		if (selectedObjects) {
			_.each(selectedObjects, (object) => {
				const pictureObj = object as IShape;
				if (pictureObj && pictureObj.isPicture === true) {
					const fill = pictureObj.spPr.fill as IPictureFillProperties;
					imageLinks.push(fill.blip.link);
				}
			});

			return imageLinks;
		}

		return undefined;
	},

	/**
     * Return user shape default properties
     * @return {IShapeProperties}
     */
	getShapeDefaults: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		return state.selection.present.defaultShapeProperties;
	},

	/**
     * Return user shape default properties
     * @return {IShapeProperties}
     */
	getShapeTextStyles: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		return state.selection.present.shapeFirstCharStyles;
	},

	/**
     * Return user text shape default properties
     * @return {IShapeProperties}
     */
	getTextShapeDefaults: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		return state.selection.present.defaultTextShapeProperties;
	},

	/**
     * Return zoom factor of canvas
     * @return {number}
     */
	getZoomFactor: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		return state.canvasEditor.zoomFactor;
	},

	/**
     * Return true if the the shape being drawn is an image placeholder
     * @return {boolean}
     */
	isDrawPlaceholder: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const drawTool = state.selection.present.shapeDrawTools;
		return drawTool && drawTool.isPlaceholder;
	},

	/**
     * Return format painter active flag
     * @return {boolean}
     */
	isFormatPainterSelected: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		return state.canvasEditor.isFormatPainterActive;
	},

	/**
     * Return true if a group is selected.
     * @returns {boolean}
     */
	isGroupSelected: (): boolean => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);

		if (selectedObjects && selectedObjects.length === 1) {
			return !!((selectedObjects[0] as IGroupShape).childObjects);
		}
		return false;
	},

	/**
     * Return whether shape is picture or not
     * @param pageObject {PageObjectType}
     * @return {boolean}
     */
	isPicture: (pageObject: PageObjectType) => {
		return ((pageObject.type === IPageObjectTypes.SHAPE && pageObject.isPicture)
            || pageObject.type === IPageObjectTypes.PICTURE);
	},

	/**
     * Return true if the selected shape is an attribute placeholder,
     * or a group containing at least one attribute placeholder.
     * @return {boolean}
     */
	isSelectedShapeAttributePlaceholder: (): boolean => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);

		if (selectedObjects) {
			let allPlaceholderObjects: any[] = [];

			selectedObjects.forEach((selectedObject: any) => {
				allPlaceholderObjects = allPlaceholderObjects.concat(getPageAttributePlaceholderObjects(selectedObject));
			});

			return allPlaceholderObjects.length > 0;
		}
		return false;
	},

	/**
     * Return true if the selected shape is custom
     * @return {boolean}
     */
	isSelectedShapeCustom: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);

		if (selectedObjects && selectedObjects.length === 1) {
			const realObject = selectedObjects[0].spPr as IShapeProperties|IConnectionShapeProperties;

			if (realObject) {
				return realObject.geometry.type === "CUSTOM";
			}
		}
		return false;
	},

	/**
     * Return true if the selected shape is created from an image placeholder
     * @return {boolean}
     */
	isSelectedShapeFromPlaceholder: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);

		if (selectedObjects) {
			let allPlaceholderObjects: any[] = [];

			selectedObjects.forEach((selectedObject: object) => {
				allPlaceholderObjects = allPlaceholderObjects.concat(getPageFromImagePlaceholderObjects
				(selectedObject));
			});

			return allPlaceholderObjects.length > 0;
		}
		return false;
	},

	/**
     * Return true if the selected shape is an image placeholder
     * @return {boolean}
     */
	isSelectedShapeImagePlaceholder: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);

		if (selectedObjects) {
			let allPlaceholderObjects: any[] = [];

			selectedObjects.forEach((selectedObject: object) => {
				allPlaceholderObjects = allPlaceholderObjects.concat(getPageImagePlaceholderObjects(selectedObject));
			});

			return allPlaceholderObjects.length > 0;
		}
		return false;
	},

	/**
     * Return true if the selected shape is an image placeholder,
     * or an attribute placeholder, or a group containing at least
     * one image or attribute placeholder.
     * @return {boolean}
     */
	isSelectedShapePlaceholder: (): boolean => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);

		if (selectedObjects) {
			let allPlaceholderObjects: any[] = [];

			selectedObjects.forEach((selectedObject: any) => {
				allPlaceholderObjects = allPlaceholderObjects.concat(getPagePlaceholderObjects(selectedObject));
			});

			return allPlaceholderObjects.length > 0;
		}
		return false;
	},

	/**
     * Return true if the selected shape is rounded
     * @return {boolean}
     */
	isSelectedShapeRounded: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);

		if (selectedObjects && selectedObjects.length === 1) {
			const realObject = selectedObjects[0].spPr as IShapeProperties|IConnectionShapeProperties;

			if (realObject && realObject.geometry) {
				if (realObject.geometry.type === "CUSTOM") {
					return false;
				}

				const presetGeometry = realObject.geometry as IPresetGeometry;

				if (presetGeometry.presetShape === "ellipse" || presetGeometry.presetShape === "roundRect") {
					return true;
				}
			}
		}
		return false;
	},

	/**
     * Return true if all selected shapes support arrow otherwise return false.
     * @return {boolean}
     */
	isSelectedShapesSupportArrow: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);
		// If no shape is selected arrow option should be enable in it's custom shape or line shape
		// For Custom shape shapeDrawTools is undefined so checking it below.
		if (!selectedObjects || !selectedObjects.length) {
			return !state.selection.present.shapeDrawTools
                || (state.selection.present.shapeDrawTools.shapeType
                    && state.selection.present.shapeDrawTools.shapeType === ShapeType.line);
		}
		return supportsArrow(selectedObjects);
	},

	/**
     * Return true if the selected shape supports edit points
     * @return {boolean | null}
     */
	isSelectedShapesSupportEditPoints: (): boolean | null => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);
		let supportsEditPoints = 0;

		if (selectedObjects) {
			selectedObjects.forEach((selectedObject: any) => {
				if (
					!selectedObject.isTextShape &&
                    !selectedObject.isPicture &&
                    !SelectionHelper.isSelectedShapeRounded() &&
                    (
                    	selectedObject.type === IPageObjectTypes.SHAPE ||
                        selectedObject.type === IPageObjectTypes.CONNECTION_SHAPE
                    )
				) {
					supportsEditPoints++;
				}
			});
		}
		return selectedObjects && selectedObjects.length === supportsEditPoints && supportsEditPoints > 0;
	},

	/**
     * Return true if all selected shapes support fill otherwise return false.
     * @return {boolean}
     */
	isSelectedShapesSupportFill: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);
		// If no shape is selected, then fill option should be enable if shape draw tool is other than line.
		if (!selectedObjects || !selectedObjects.length) {
			return (state.selection.present.shapeDrawTools
                && state.selection.present.shapeDrawTools.shapeType);
		}
		// return true if all selected shapes support fill
		return supportsFill(selectedObjects);
	},

	/**
     * Return true if the selected shape is a group and sub-element is selected
     * @return {boolean}
     */
	isSubgroupSelected: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;

		return state.selection.present.groupSelectionIndices
                && state.selection.present.groupSelectionIndices.length > 0;
	},

	/**
     * Return whether text mode is selected or not
     * @return {boolean}
     */
	isTextDrawMode: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const drawTool = state.selection.present.shapeDrawTools;
		return drawTool && drawTool.isTextShape;
	},

	/**
     * Prepare page for copy, create deep clone, generate new UUID for each shape.
     * @param {IPage} page
     * @returns {IPage}
     */
	preparePageForCopy: (page: IPage): IPage => {
		const newPage = _.cloneDeep(page);
		_.each(newPage.pageObjects, (pageObject) => {
			pageObject.shapeId = generateUUID();
		});
		// generate new ids for all shapes.
		return newPage;
	},

	/**
     * Return selected page objects
     * @return {PageObjectType[]}
     */
	selectedBaseObjects: () => getSelectedBaseObjects(getStore()
		.getState() as ICanvasEditorState),

	/**
     * Return selected page objects
     * @return {PageObjectType[]}
     */
	selectedObjects: () => getSelectedPageObjects(getStore()
		.getState() as ICanvasEditorState),

	/**
     * Return selected page
     * @return {IPage}
     */
	selectedPage: () => getSelectedPage(getStore()
		.getState() as ICanvasEditorState),

	/**
      * Return primary selected object
      * @return {PageObjectType}
      */
	selectedPrimaryObject: () => {
		const allObjects = getSelectedPageObjects(getStore()
			.getState() as ICanvasEditorState);
		return allObjects && allObjects.length ? allObjects[0] : null;
	},

	/**
     * Return whether selected shape contains text
     * @return {Boolean}
     */
	selectedShapeHasText: () => {
		const state = getStore()
			.getState() as ICanvasEditorState;
		const selectedObjects = getSelectedBaseObjects(state);

		if (selectedObjects && selectedObjects.length === 1) {
			const realObject = selectedObjects[0] as IShape;

			if (realObject.txBody && realObject.txBody.content) {
				// Strips html tags and their contents out of the text, leaving only actual text content.
				let textString = realObject.txBody.content.replace(/<[^>]*>?/gm, "");
				textString = removeZeroSpaceWidth(textString);

				return textString.length > 0;
			}
		}

		return false;
	},
};
